import moment from 'moment'
import {
  getShiftIdentityKey,
  isMergeableApplicationShift,
  isShiftAlreadyFilled,
} from '../../../helpers/shiftIdentity'

// Turns a flat list of pending applications (GET /shift-applications) into
// review rows. The server paginates per row, so regrouping here reproduces
// those rows exactly - see the merged-vacancy grouping notes in
// helpers/shiftIdentity.js. Shared by the Shift Applications page and the
// event page's Shifts panel.
//   mergeable     - one open vacancy advertised as N identical shifts
//   alreadyFilled - stranded applications against a shift filled elsewhere
//   otherwise     - a single application (incl. swap requests)
export const groupShiftApplications = (applications) => {
  const groups = new Map()

  applications.forEach((application) => {
    const mergeable = isMergeableApplicationShift(application.shift)
    const alreadyFilled = !mergeable && isShiftAlreadyFilled(application.shift)
    const key = mergeable
      ? getShiftIdentityKey(application.shift)
      : alreadyFilled
        ? `filled-${application.shift?._id}`
        : `single-${application._id}`

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        mergeable,
        alreadyFilled,
        shift: application.shift,
        applications: [],
      })
    }
    groups.get(key).applications.push(application)
  })

  // Count unique applicants, not raw applications - legacy duplicates can
  // leave one person pending more than once against a merged vacancy.
  groups.forEach((group) => {
    const uniqueEmployeeIDs = new Set(
      group.applications.map((application) =>
        String(application.employee?._id ?? application.employee),
      ),
    )
    group.applicantCount = uniqueEmployeeIDs.size
  })

  return [...groups.values()].sort(
    (a, b) => moment(a.shift.start).valueOf() - moment(b.shift.start).valueOf(),
  )
}

export const getApplicantLabel = (group) => {
  const representative = group.applications[0]
  const applicantName =
    `${representative?.employee?.userAccount?.firstName ?? ''} ${
      representative?.employee?.userAccount?.lastName ?? ''
    }`.trim() || 'Applicant'
  return group.applicantCount > 1
    ? `${group.applicantCount} applicants`
    : applicantName
}

// A swap request: someone applying for a colleague's offered shift
export const isSwapGroup = (group) =>
  !!(group.shift?.availableForReassignment && group.shift?.assignedTo)
